import React, { useEffect, useMemo, useState } from "react";
import "leaflet/dist/leaflet.css";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { resolveBySerial, getPermissionsWithUsers } from "../../utils/padlock";

import { fmtDate, fmtBattery, fmtCoords, isNum } from "../../utils/format";

import {
  MapContainer,
  TileLayer,
  Marker,
  CircleMarker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";

// --- componente auxiliar para mover el mapa ---
function MapMover({ center }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.flyTo(center, 12);
    }
  }, [center, map]);

  return null;
}

// -- Componente para las fechas --
function parseDateTimeInput(value, isEnd = false) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;

  // datetime-local usually lacks seconds (YYYY-MM-DDTHH:mm)
  const timePart = (value.split("T")[1] || "").trim();
  const hasSeconds = timePart.split(":").length >= 3;
  if (!hasSeconds) {
    if (isEnd) d.setSeconds(59, 999);
    else d.setSeconds(0, 0);
  }

  return d;
}

function filterByDate(arr, fromDateTime, toDateTime) {
  const from = parseDateTimeInput(fromDateTime, false);
  const to = parseDateTimeInput(toDateTime, true);

  return arr.filter((e) => {
    const t = new Date(e.capturedAt || e.openedAt);
    if (!t || isNaN(t)) return false;

    if (from && t < from) return false;
    if (to && t > to) return false;

    return true;
  });
}

function getRouteGradientColor(position, total) {
  if (total <= 1) return "hsl(0 85% 50%)";

  const ratio = position / (total - 1);
  const hue = Math.round(220 * ratio);

  return `hsl(${hue} 85% 50%)`;
}

const MAP_POINT_LIMIT_OPTIONS = [100, 250, 500, 1000, 2000];
const MAX_ROUTE_ARROWS = 24;

function getSegmentAngle(from, to) {
  const deltaLat = Number(to.lat) - Number(from.lat);
  const deltaLng = Number(to.lng) - Number(from.lng);
  return (Math.atan2(deltaLng, deltaLat) * 180) / Math.PI;
}

function buildRouteArrows(points, maxArrows = MAX_ROUTE_ARROWS) {
  if (!Array.isArray(points) || points.length < 2) return [];

  const segmentCount = points.length - 1;
  const step = Math.max(1, Math.ceil(segmentCount / maxArrows));
  const arrows = [];

  for (let i = 0; i < segmentCount; i += step) {
    const from = points[i];
    const to = points[i + 1];

    arrows.push({
      id: `arrow-${from.id}-${to.id}`,
      lat: (Number(from.lat) + Number(to.lat)) / 2,
      lng: (Number(from.lng) + Number(to.lng)) / 2,
      angle: getSegmentAngle(from, to),
    });
  }

  return arrows;
}

function createRouteArrowIcon(angle) {
  return L.divIcon({
    className: "",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    html: `
      <div style="width:18px;height:18px;display:flex;align-items:center;justify-content:center;transform:rotate(${angle}deg);">
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path d="M9 1 L16 16 L9 12.5 L2 16 Z" fill="#2563eb" fill-opacity="0.95" stroke="#ffffff" stroke-width="1.2" />
        </svg>
      </div>
    `,
  });
}

function createOpenMarkerIcon({ variant = "default", authenticated = false } = {}) {
  const color =
    variant === "latest"
      ? "#2563eb"
      : authenticated
        ? "#16a34a"
        : "#ef4444";
  const size = variant === "latest" ? 34 : 28;
  const anchorX = size / 2;
  const anchorY = size - 2;

  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [anchorX, anchorY],
    popupAnchor: [0, -size + 8],
    html: `
      <div style="width:${size}px;height:${size}px;display:flex;align-items:flex-end;justify-content:center;">
        <svg width="${size}" height="${size}" viewBox="0 0 34 34" aria-hidden="true">
          <defs>
            <filter id="open-pin-shadow-${variant}-${authenticated ? "auth" : "anon"}" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(15,23,42,0.35)" />
            </filter>
          </defs>
          <g filter="url(#open-pin-shadow-${variant}-${authenticated ? "auth" : "anon"})">
            <path d="M17 2.5C10.1 2.5 4.5 8.1 4.5 15c0 8.7 10.1 15.7 11.4 16.6a1.9 1.9 0 0 0 2.2 0C19.4 30.7 29.5 23.7 29.5 15 29.5 8.1 23.9 2.5 17 2.5Z" fill="${color}" />
            <circle cx="17" cy="14.5" r="8.4" fill="#ffffff" />
            <path d="M14.2 13.3v-1c0-1.7 1.3-3 2.8-3s2.8 1.3 2.8 3v1" fill="none" stroke="${color}" stroke-width="1.9" stroke-linecap="round" />
            <rect x="12.5" y="13.1" width="9" height="7.6" rx="2.1" fill="${color}" />
            <path d="M18.8 16.8a1.8 1.8 0 1 0-3.6 0c0 .8.5 1.4 1.1 1.7v1.4h1.4v-1.4c.6-.3 1.1-.9 1.1-1.7Z" fill="#ffffff" />
          </g>
          <circle cx="17" cy="14.5" r="${variant === "latest" ? "12.5" : "10.8"}" fill="none" stroke="${color}" stroke-opacity="${variant === "latest" ? "0.18" : "0.12"}" stroke-width="${variant === "latest" ? "4" : "3"}" />
        </svg>
      </div>
    `,
  });
}

function getLastRoutePoints(points, limit) {
  if (!Array.isArray(points) || points.length === 0) return [];

  if (!Number.isFinite(limit) || limit <= 0 || points.length <= limit) {
    return points.map((point, position) => ({
      ...point,
      routeOrder: position + 1,
      routeColor: getRouteGradientColor(position, points.length),
    }));
  }

  const sampled = points.slice(-limit);

  return sampled.map((point, position) => ({
    ...point,
    routeOrder: position + 1,
    routeColor: getRouteGradientColor(position, sampled.length),
  }));
}

export default function DashboardPadlockIDSuper() {
  const { serialNumber } = useParams();
  const navigate = useNavigate();

  const [padlockId, setPadlockId] = useState(null);
  const [data, setData] = useState(null);

  const [track, setTrack] = useState([]);
  const [historicRoute, setHistoricRoute] = useState([]);
  const [basicLocationRows, setBasicLocationRows] = useState([]);

  const [resolving, setResolving] = useState(true);
  const [loadingDash, setLoadingDash] = useState(true);
  const [err, setErr] = useState(null);

  // --- permisos ---
  const [perms, setPerms] = useState([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsErr, setPermsErr] = useState(null);

  // --- aperturas reales desde openEvent ---
  const [openEvents, setOpenEvents] = useState([]);
    // --- estado para cuando seleccionas coordenadas desde la tabla ---
  const [selectedCoords, setSelectedCoords] = useState(null);

  // --- paginación ---
  const [pageTable, setPageTable] = useState(1);
  const pageSize = 10;

  // --- filtros de tabla ---
  const [tableMode, setTableMode] = useState("all");
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");

  // Switch con ORS - sin ORS
  const [showORS, setShowORS] = useState(false);
  const [orsRoute, setOrsRoute] = useState([]);

  const [showLegend, setShowLegend] = useState(false);
  const [mapPointLimit, setMapPointLimit] = useState(1000);


  /////////////////////////////////////////////////////////
  /////////////////////// LOGICA /////////////////////////
  ////////////////////////////////////////////////////////

  // 1. Resolver ID desde serialNumber
  useEffect(() => {
    let alive = true;
    async function resolveId() {
      setResolving(true);
      setErr(null);
      try {
        const found = await resolveBySerial(serialNumber);
        if (!alive) return;
        if (found?.id) setPadlockId(found.id);
        else
          setErr(new Error("No se pudo resolver el candado por serialNumber."));
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setResolving(false);
      }
    }
    if (serialNumber) resolveId();
    else {
      setErr(new Error("Falta el serialNumber en la URL."));
      setResolving(false);
    }
    return () => {
      alive = false;
    };
  }, [serialNumber]);

  // 2. Cargar dashboard 
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchDashboard(id) {
      setLoadingDash(true);
      setErr(null);
      try {
        const res = await api.get(endpoints.padlock.dashboard(id));
        const payload = res.data?.data ?? res.data;
        if (alive) setData(payload);
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setLoadingDash(false);
      }
    }
    fetchDashboard(padlockId);
    return() => (alive = false);
  }, [padlockId]);

  // 3. Cargar permisos
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchPerms(id) {
      setPermsLoading(true);
      try {
        const arr = await getPermissionsWithUsers(id);
        if (alive) setPerms(arr);
      } catch (e) {
        if (alive) setPermsErr(e);
      } finally {
        if (alive) setPermsLoading(false);
      }
    }
    fetchPerms(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 4. Ruta básica para tabla
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;

    async function fetchBasicRoute(id) {
      try {
        const res = await api.get(endpoints.padlock.routeBasic(id));
        const arr = res.data?.data ?? res.data ?? [];

        if (!alive) return;

        const rows = arr
          .filter(e => isNum(e.latitude) && isNum(e.longitude))
          .map(e => ({
            id: `basic-${e.capturedAt}`,
            capturedAt: e.capturedAt,
            uploadedAt: e.uploadedAt,
            eventType: "Localización",
            transport: e.transport ?? "—",
            user: "—",
            lat: Number(e.latitude),
            lng: Number(e.longitude),
            battery:
              e.batteryPct != null
                ? Number(e.batteryPct)
                : "-",
            detail: e.motion_status ?? null,
          }));
        setBasicLocationRows(rows);
      } catch (e) {
        console.error("Error cargando ruta básica", e);
      }
    }
    fetchBasicRoute(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 5. Ruta historica completa
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchRoute(id) {
      try {
        const res = await api.get(endpoints.padlock.route(id));
        const arr = res.data?.data ?? res.data ?? [];
        if (!alive) return;

        const pts = arr
          .filter((e) => isNum(e.latitude) && isNum(e.longitude))
          .map((e) => ({
            lat: Number(e.latitude),
            lng: Number(e.longitude),
            capturedAt: e.capturedAt,
            transport: e.transport ?? null,
            motion_status: e.motion_status ?? null,
            location: e.location ?? null,
          }));
        setHistoricRoute(pts);
      } catch (e) {
        console.error("Error cargando ruta histórica", e);
      }
    }
    fetchRoute(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 6. Ruta reciente
  useEffect(() => {
    const nh = (data?.recent?.notehub || []).filter(
      (e) => isNum(e?.latitude) && isNum(e?.longitude)
    );

    if (nh.length > 0) {
      const pts = nh
        .map((e) => ({
          lat: Number(e.latitude),
          lng: Number(e.longitude),
          at: e.eventAt ?? null,
          speed: e.speed ?? null,
        }))
        .sort((a, b) => new Date(a.at) - new Date(b.at));
      setTrack(pts);
    } else if (
      data?.lastLocation &&
      isNum(data.lastLocation.latitude) &&
      isNum(data.lastLocation.longitude)
    ) {
      setTrack([
        {
          lat: Number(data.lastLocation.latitude),
          lng: Number(data.lastLocation.longitude),
          at: data.lastLocation.eventAt ?? null,
          speed: data.lastLocation.speed ?? null,
        },
      ]);
    } else {
      setTrack([]);
    }
  }, [data?.recent?.notehub, data?.lastLocation]);

  // 7. Cargar aperturas reales
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchOpens(id) {
      try {
        const res = await api.get(endpoints.padlock.openEvents(id));
        if (alive) setOpenEvents(res.data ?? []);
      } catch (e) {
        console.error("Error cargando aperturas", e);
      }
    }
    fetchOpens(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 8. Ruta ORS
  useEffect(() => {
    if (!showORS || !padlockId) return;

    async function loadORS() {
      try {
        const res = await api.get(endpoints.padlock.orsRoute(padlockId));
        setOrsRoute(res.data?.route ?? []);
      } catch (err) {
        console.error("Error cargando ORS:", err);
      }
    }

    loadORS();
  }, [showORS, padlockId]);


  /////////////////////////////////////////
  ///////////// Derivados ////////////////
  ////////////////////////////////////////
  const padlock = data?.padlock;
  const lastTelemetry = data?.lastTelemetry;
  const lastLocation = data?.lastLocation;
  const metrics = data?.metrics;

  // Movimiento
  const inMotion =
    lastTelemetry?.inMotion === true ||
    (typeof lastLocation?.speed === "number" && lastLocation.speed > 0) ||
    padlock?.status === "MOVING";

  const motionLabel = (motion) => {
    const raw = String(motion || "").toLowerCase();
    if (raw.includes("stop")) return "Parado";
    if (raw.length > 0) return "En movimiento";
    return "—";
  };

  // Markers de apertura
  const openMarkers = useMemo(() => {
    return openEvents
      .filter((o) => isNum(o?.latitude) && isNum(o?.longitude))
      .map((o) => ({
        id: o.id,
        lat: Number(o.latitude),
        lng: Number(o.longitude),
        at: o.openedAt,
        by: o.author ?? "—",
      }));
  }, [openEvents]);

  const filteredOpenMarkers = useMemo(() => {
    const from = parseDateTimeInput(fromDateTime, false);
    const to = parseDateTimeInput(toDateTime, true);

    return openMarkers.filter((o) => {
      const t = new Date(o.at);
      if (!t || isNaN(t)) return false;
      if (from && t < from) return false;
      if (to && t > to) return false;
      return true;
    });
  }, [openMarkers, fromDateTime, toDateTime]);

  // Ultima apertura filtrada
  const lastOpenMarker = useMemo(() => {
    if (!filteredOpenMarkers.length) return null;
    return filteredOpenMarkers.reduce((latest, curr) =>
      new Date(curr.at) > new Date(latest.at) ? curr : latest
    );
  }, [filteredOpenMarkers]);

  // Fila de aperturas para la tabla
  const openRows = useMemo(
    () =>
      openEvents.map((ev) => ({
        id: `open-${ev.id}`,
        capturedAt: ev.openedAt ?? null,
        uploadedAt: ev.createdAt ?? null,
        eventType: "Apertura",
        detail: motionLabel(ev.motion_status ?? ev.motion),
        transport: ev.transport ?? null,
        user: ev.author ?? "—",
        lat: isNum(ev.latitude) ? Number(ev.latitude) : null,
        lng: isNum(ev.longitude) ? Number(ev.longitude) : null,
        battery:
          ev.batteryPct != null
            ? Number(ev.batteryPct)
            : "-",
      })),
    [openEvents]
  );

  const locationRows = useMemo(() => basicLocationRows, [basicLocationRows]);

  // Filtrar la tabla
  const tableRows = useMemo(() => {
    const rows = [];

    if (tableMode !== "coords") rows.push(...openRows);
    if (tableMode !== "apertures") rows.push(...locationRows);

    const filtered = filterByDate(rows, fromDateTime, toDateTime);

    const sorted = filtered.sort(
      (a, b) => new Date(b.capturedAt || 0) - new Date(a.capturedAt || 0)
    );

    const total = sorted.length;

    return sorted.map((row, idx) => ({
      ...row,
      index: total - idx,
    }));
  }, [locationRows, openRows, tableMode, fromDateTime, toDateTime]);

  const routePoints = useMemo(() => {
    const points = tableRows
      .filter((row) => isNum(row.lat) && isNum(row.lng))
      .sort((a, b) => a.index - b.index);

    return points.map((point, position) => ({
      ...point,
      routeOrder: position + 1,
      routeColor: getRouteGradientColor(position, points.length),
    }));
  }, [tableRows]);

  const displayedRoutePoints = useMemo(
    () => getLastRoutePoints(routePoints, mapPointLimit),
    [routePoints, mapPointLimit]
  );

  const hiddenRoutePointsCount = Math.max(
    routePoints.length - displayedRoutePoints.length,
    0
  );

  const routeArrows = useMemo(
    () => buildRouteArrows(displayedRoutePoints),
    [displayedRoutePoints]
  );

  // Paginacion
  const paginatedRows =
    tableRows.slice(
      (pageTable - 1) * pageSize,
      pageTable * pageSize);

  const totalTablePages = Math.ceil(tableRows.length / pageSize);

  // Reset page al cambiar modo
  useEffect(() => {
    setPageTable(1);
  }, [tableMode]);

  // --- última localización calculada ---
  const ultimaLocalizacion = useMemo(() => {
    // 1. Si existe última localización en la telemetría -> siempre prioridad
    if (
      data?.lastLocation &&
      isNum(data.lastLocation.latitude) &&
      isNum(data.lastLocation.longitude)
    ) {
      return [
        Number(data.lastLocation.latitude),
        Number(data.lastLocation.longitude),
      ];
    }

    // 2. Si hay track reciente
    if (track.length > 0) {
      const last = track[track.length - 1];
      return [last.lat, last.lng];
    }

    // 3. Si hay aperturas
    if (openEvents.length > 0) {
      const last = openEvents[0]; // vienen ordenados desc
      return [Number(last.latitude), Number(last.longitude)];
    }

    // 4. Fallback Madrid
    return [40.4167, -3.70325];
  }, [data?.lastLocation, track, openEvents]);

  const isLoading = resolving || loadingDash;

  const EVENT_COLORS = {
    Apertura: {
      row: "bg-red-50 hover:bg-red-100",
      button: "border-red-500 text-red-700",
      buttonActive: "bg-red-400 text-white border-red-500",
    },
    Localización: {
      row: "bg-blue-50 hover:bg-blue-100",
      button: "border-blue-500 text-blue-700",
      buttonActive: "bg-blue-400 text-white border-blue-500",
    },
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Dispositivo #{padlock?.id ?? "—"} — {padlock?.name ?? "—"}
        </h1>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded border text-blue-600"
            onClick={() =>
              navigate(`/padlocks/${encodeURIComponent(serialNumber)}/permissions`)
            }
            disabled={!padlock?.id}
          >
            Dar permisos
          </button>
          <button className="px-3 py-2 rounded border" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </div>

      {isLoading && <div className="text-gray-500">Cargando…</div>}

      {err && (
        <div className="text-red-600">
          <p className="font-semibold">Error</p>
          <pre className="whitespace-pre-wrap text-sm">{String(err)}</pre>
        </div>
      )}

      {!isLoading && !err && data && (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Estado */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Estado</div>
              <div
                className={`text-lg font-semibold ${
                  inMotion
                    ? "text-blue-600"
                    : padlock?.status === "ACTIVE"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {inMotion ? "En movimiento" : padlock?.status ?? "—"}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Serial: {padlock?.serialNumber ?? serialNumber}
              </div>
              <div className="text-xs text-gray-400">
                Compañía: {padlock?.company?.name ?? "—"}
              </div>
            </div>

            {/* Batería */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Batería</div>
              <div className="text-lg font-semibold">
                {lastTelemetry?.batteryPct != null
                  ? fmtBattery(lastTelemetry.batteryPct)
                  : "—"}
              </div>
              <div className="text-xs text-gray-400">
                Última sync: {fmtDate(lastTelemetry?.lastSyncAt)}
              </div>
            </div>

            {/* Última posición */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Última posición</div>
              <div className="text-lg font-semibold">
                {lastLocation?.location ?? "—"}
              </div>
              <div className="text-xs text-gray-400">
                {fmtCoords(lastLocation?.latitude, lastLocation?.longitude)}
              </div>
              <div className="text-xs text-gray-400">
                {fmtDate(lastLocation?.createdAt ?? lastTelemetry?.lastSyncAt)}
              </div>
            </div>

            {/* Actividad */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Actividad</div>
              <div className="text-lg font-semibold">
                Aperturas: {openEvents.length ?? 0}
              </div>
              <div className="text-xs text-gray-400">
                Telemetrías: {metrics?.totalTelemetries ?? 0}
              </div>
            </div>
          </div>

          {/* Mapa */}
          <div className="bg-white p-4 rounded shadow">
            <div className="flex items-center gap-2 mb-2 relative">
              <h3 className="font-semibold">Mapa</h3>

              {/* Botón "i" */}
              <button
                onClick={() => setShowLegend((v) => !v)}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-xs hover:bg-gray-300"
                title="Mostrar leyenda"
              >
                i
              </button>

              {/* Popup leyenda */}
              {showLegend && (
                <div className="absolute top-6 left-0 bg-white shadow-lg rounded-md p-3 text-xs z-50 w-48">
                  <p className="font-semibold text-gray-700 mb-2">Leyenda</p>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-blue-700 inline-block" />
                    Ruta reciente
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="w-10 h-3 rounded-full inline-block border border-gray-200"
                      style={{
                        background:
                          "linear-gradient(90deg, hsl(0 85% 50%) 0%, hsl(220 85% 50%) 100%)",
                      }}
                    />
                    Recorrido: inicio rojo, fin azul
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-blue-600 inline-block" />
                    Última apertura
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                    Apertura
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block" />
                    Última ubicación
                  </div>

                </div>
              )}
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Ultimos puntos en mapa</label>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={mapPointLimit}
                  onChange={(e) => setMapPointLimit(Number(e.target.value))}
                >
                  {MAP_POINT_LIMIT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-gray-500">
                Mostrando los ultimos {displayedRoutePoints.length} de {routePoints.length} puntos
                {hiddenRoutePointsCount > 0 ? ` (${hiddenRoutePointsCount} ocultos)` : ""}
              </div>
            </div>

            <MapContainer
              center={ultimaLocalizacion}
              zoom={12}
              style={{ height: "400px", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              {/* este mueve el mapa cuando seleccionas coords desde la tabla */}
              <MapMover center={selectedCoords || ultimaLocalizacion} />
 
              {/**Ruta */}
              {displayedRoutePoints.length > 1 && (
                <Polyline
                  positions={displayedRoutePoints.map((point) => [Number(point.lat), Number(point.lng)])}
                  color="#2563eb"
                  weight={3}
                  opacity={0.45}
                />
              )}
              {routeArrows.map((arrow) => (
                <Marker
                  key={arrow.id}
                  position={[arrow.lat, arrow.lng]}
                  icon={createRouteArrowIcon(arrow.angle)}
                  interactive={false}
                />
              ))}


              {/* Puntos históricos del recorrido */}
              {displayedRoutePoints.map((p) => (
                  <CircleMarker
                    key={`row-${p.id}`}
                    center={[Number(p.lat), Number(p.lng)]}
                    radius={5}
                    pathOptions={{
                      color: "#ffffff",
                      weight: 1.5,
                      fillColor: p.routeColor,
                      fillOpacity: 0.95,
                    }}
                  >
                    <Popup>
                      <div className="bg-white p-2 text-sm leading-relaxed">

                        <div className="font-semibold text-base mb-1">
                          Punto #{p.routeOrder} — {p.eventType}
                        </div>

                        <div>
                          Hora: <span className="font-medium">
                            {fmtDate(p.capturedAt)}
                          </span>
                        </div>

                        <div>
                          Coordenadas:
                          <span className="font-medium">
                            {fmtCoords(p.lat, p.lng)}
                          </span>
                        </div>

                        {p.transport && (
                          <div>
                            Transporte: <span className="font-medium">{p.transport}</span>
                          </div>
                        )}

                        {p.user && (
                          <div>
                            Usuario: <span className="font-medium">{p.user}</span>
                          </div>
                        )}

                      </div>
                    </Popup>
                  </CircleMarker>
              ))}

              {/* Aperturas */}
              {filteredOpenMarkers.map((o) => (
                <Marker
                  key={`open-${o.id}`}
                  position={[o.lat, o.lng]}
                  icon={createOpenMarkerIcon({ authenticated: Boolean(o.auth) })}
                >
                  <Popup>
                    <div>
                      <strong>Apertura</strong>
                      <br />
                      {fmtDate(o.at)}
                      <br />
                      <strong>Usuario: </strong>
                      {o.by}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Última apertura destacada */}
              {lastOpenMarker && (
                <Marker
                  position={[lastOpenMarker.lat, lastOpenMarker.lng]}
                  icon={createOpenMarkerIcon({ variant: "latest" })}
                >
                  <Popup>
                    <div>
                      <strong>Última apertura</strong>
                      <br />
                      {fmtDate(lastOpenMarker.at)}
                      <br /> <strong>Usuario: </strong>
                      {lastOpenMarker.by}
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Ruta de track reciente */}
              {track.length >= 1 && (
                <Polyline
                  positions={track.map((p) => [p.lat, p.lng])}
                  color="blue"
                  weight={3}
                  opacity={0.6}
                />
              )}
              {/* Última localización conocida */}
              {lastLocation &&
                isNum(lastLocation.latitude) &&
                isNum(lastLocation.longitude) && (
                  <Marker
                    position={[Number(lastLocation.latitude), Number(lastLocation.longitude)]}
                    icon={L.icon({
                      iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/yellow-dot.png",
                      iconSize: [32, 32],
                    })}
                  >
                    <Popup>
                      <div className="bg-white p-4 text-sm leading-relaxed">
                        <div className="font-semibold text-base mb-2">
                          {padlock?.serialNumber ?? "—"}{" "}
                        </div>

                        {/* Estado de movimiento */}
                        <div className="flex items-center gap-2 mb-1">
                          {String(
                            lastTelemetry?.motion ||
                            lastLocation?.motion_status ||
                            ""
                          ).toLowerCase().includes("stopped") ? (
                            <>
                              <span role="img" aria-label="parado">🟥</span>
                              <span>Parado</span>
                            </>
                          ) : (
                            <>
                              <span role="img" aria-label="en movimiento">🟩</span>
                              <span>En movimiento</span>
                            </>
                          )}
                        </div>

                        {/* Coordenadas */}
                        <div>
                          Coordenadas:{" "}
                          <span className="font-medium">
                            {fmtCoords(lastLocation?.latitude, lastLocation?.longitude)}
                          </span>
                        </div>

                        {/* Último evento */}
                        <div>
                          Último evento:{" "}
                          <span className="font-medium">{fmtDate(lastLocation?.eventAt)}</span>
                        </div>

                        {/* Batería */}
                        <div>
                          Batería:{" "}
                          <span className="font-medium">
                            {lastTelemetry?.batteryPct != null ? `${lastTelemetry.batteryPct}%` : "—"}
                          </span>
                        </div>

                        {/* Última sync */}
                        <div>
                          Última sync:{" "}
                          <span className="font-medium">{fmtDate(lastTelemetry?.lastSyncAt)}</span>
                        </div>

                        {/* Ubicación */}
                        <div>
                          Ubicación:{" "}
                          <span className="font-medium">
                            {lastLocation?.location || "—"}
                          </span>
                        </div>

                        {/* Transporte */}
                        {(lastTelemetry?.transport || lastLocation?.transport) && (
                        <div>
                          Transporte:{" "}
                          <span className="font-medium flex items-center gap-2">
                            {String(lastTelemetry?.transport || lastLocation?.transport)
                              .toLowerCase()
                              .includes("cell") ? (
                              <>
                                <span role="img" aria-label="celular">
                                  📱
                                </span>
                                <span>Celular (LTE)</span>
                              </>
                            ) : String(lastTelemetry?.transport || lastLocation?.transport)
                                .toLowerCase()
                                .includes("skylo") ? (
                              <>
                                <span role="img" aria-label="satelite">
                                  🛰️
                                </span>
                                <span>Satélite (Skylo)</span>
                              </>
                            ) : (
                              <>
                                <span role="img" aria-label="desconocido">
                                  ❔
                                </span>
                                <span>{lastTelemetry?.transport || lastLocation?.transport}</span>
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      </div>
                    </Popup>
                  </Marker>
                )}
            </MapContainer>
          </div>

          {/* --- Tabla aperturas --- */}
          <div className="bg-white p-4 rounded shadow">
            <div className="hidden md:flex md:items-start md:justify-between md:gap-4">
              <div>
                <h3 className="font-semibold">Eventos recientes</h3>
                <p className="text-sm text-gray-500">Aperturas y localizaciones</p>
              </div>
              <div className="flex flex-wrap items-end justify-end gap-2">
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Desde</label>
                  <input
                    type="datetime-local"
                    className="border p-2 rounded w-[230px]"
                    value={fromDateTime}
                    onChange={(e) => setFromDateTime(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Hasta</label>
                  <input
                    type="datetime-local"
                    className="border p-2 rounded w-[230px]"
                    value={toDateTime}
                    onChange={(e) => setToDateTime(e.target.value)}
                  />
                </div>
              </div>
                <div className="flex flex-wrap items-end gap-2">
                <button
                  className="px-3 py-2 rounded border border-green-500 text-green-700 hover:bg-green-50 text-sm"
                  onClick={() => {
                    if (
                      lastLocation &&
                      isNum(lastLocation.latitude) &&
                      isNum(lastLocation.longitude)
                    ) {
                      setSelectedCoords([
                        Number(lastLocation.latitude),
                        Number(lastLocation.longitude)
                      ]);
                    }
                  }}
                >
                  Ir a última localización
                </button>
                <button
                  className={`px-3 py-2 rounded border text-sm ${
                    tableMode === "apertures"
                      ? EVENT_COLORS.Apertura.buttonActive
                      : EVENT_COLORS.Apertura.button
                  }`}
                  onClick={() => setTableMode("apertures")}
                >
                  Solo aperturas
                </button>
                <button
                  className={`px-3 py-2 rounded border text-sm ${
                    tableMode === "coords"
                      ? EVENT_COLORS.Localización.buttonActive
                      : EVENT_COLORS.Localización.button
                  }`}
                  onClick={() => setTableMode("coords")}
                >
                  Solo coordenadas
                </button>
                <button
                  className={`px-3 py-2 rounded border text-sm ${
                    tableMode === "all"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "text-blue-600 border-blue-200"
                  }`}
                  onClick={() => setTableMode("all")}
                >
                  Ver todo
                </button>
                <button
                  className="px-3 py-2 rounded border border-gray-400 hover:bg-gray-100 text-sm"
                  onClick={() => {
                    setFromDateTime("");
                    setToDateTime("");
                  }}
                >
                  Limpiar filtros
                </button>
                </div>
              </div>
            </div>

            <div className="md:hidden space-y-4">
              <div>
                <h3 className="font-semibold">Eventos recientes</h3>
                <p className="text-sm text-gray-500">Aperturas y localizaciones</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Desde</label>
                  <input
                    type="datetime-local"
                    className="border p-2 rounded w-full min-w-0"
                    value={fromDateTime}
                    onChange={(e) => setFromDateTime(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Hasta</label>
                  <input
                    type="datetime-local"
                    className="border p-2 rounded w-full min-w-0"
                    value={toDateTime}
                    onChange={(e) => setToDateTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  className="px-3 py-2 rounded border border-green-500 text-green-700 hover:bg-green-50 text-sm"
                  onClick={() => {
                    if (
                      lastLocation &&
                      isNum(lastLocation.latitude) &&
                      isNum(lastLocation.longitude)
                    ) {
                      setSelectedCoords([
                        Number(lastLocation.latitude),
                        Number(lastLocation.longitude)
                      ]);
                    }
                  }}
                >
                  Ir a última localización
                </button>
                <button
                  className={`px-3 py-2 rounded border text-sm ${
                    tableMode === "apertures"
                      ? EVENT_COLORS.Apertura.buttonActive
                      : EVENT_COLORS.Apertura.button
                  }`}
                  onClick={() => setTableMode("apertures")}
                >
                  Solo aperturas
                </button>
                <button
                  className={`px-3 py-2 rounded border text-sm ${
                    tableMode === "coords"
                      ? EVENT_COLORS.Localización.buttonActive
                      : EVENT_COLORS.Localización.button
                  }`}
                  onClick={() => setTableMode("coords")}
                >
                  Solo coordenadas
                </button>
                <button
                  className={`px-3 py-2 rounded border text-sm ${
                    tableMode === "all"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "text-blue-600 border-blue-200"
                  }`}
                  onClick={() => setTableMode("all")}
                >
                  Ver todo
                </button>
                <button
                  className="px-3 py-2 rounded border border-gray-400 hover:bg-gray-100 text-sm"
                  onClick={() => {
                    setFromDateTime("");
                    setToDateTime("");
                  }}
                >
                  Limpiar filtros
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-3 md:hidden">
              {paginatedRows.length ? (
                paginatedRows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className={`w-full text-left rounded-lg border p-3 ${
                      EVENT_COLORS[row.eventType]?.row ?? "bg-white"
                    }`}
                    onClick={() => {
                      if (isNum(row.lat) && isNum(row.lng)) {
                        setSelectedCoords([Number(row.lat), Number(row.lng)]);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold">{row.eventType}</div>
                        <div className="text-xs text-gray-500">#{row.index}</div>
                      </div>
                      <div className="text-right text-sm">
                        <div>
                          {dayjs(row.capturedAt).isValid()
                            ? dayjs(row.capturedAt).format("DD/MM/YYYY")
                            : "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {dayjs(row.capturedAt).isValid()
                            ? dayjs(row.capturedAt).format("HH:mm:ss")
                            : "-"}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-sm">
                      <div>
                        <div className="text-xs text-gray-500">Hora recibida</div>
                        <div>
                          {dayjs(row.uploadedAt).isValid()
                            ? dayjs(row.uploadedAt).format("HH:mm:ss")
                            : "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Bateria</div>
                        <div>{fmtBattery(row.battery)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Transporte</div>
                        <div>{row.transport ?? "-"}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Usuario</div>
                        <div>{row.user ?? "-"}</div>
                      </div>
                    </div>

                    <div className="mt-3 text-sm">
                      <div className="text-xs text-gray-500">Coordenadas</div>
                      <div>{fmtCoords(row.lat, row.lng)}</div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-3 text-gray-500 border rounded">Sin datos</div>
              )}
            </div>

            <div className="hidden md:block overflow-x-auto mt-4">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Hora capturada</th>
                    <th className="p-2 text-left">Hora recibida</th>
                    <th className="p-2 text-left">Evento</th>
                    <th className="p-2 text-left">Transporte</th>
                    <th className="p-2 text-left">Usuario</th>
                    <th className="p-2 text-left">Latitud</th>
                    <th className="p-2 text-left">Longitud</th>
                    <th className="p-2 text-left">Batería</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length ? (
                    paginatedRows.map((row) => (
                      <tr
                        key={row.id}
                        className={`
                          border-b cursor-pointer
                          ${
                            EVENT_COLORS[row.eventType]?.row ?? "hover:bg-gray-50"
                          }
                        `}
                        onClick={() => {
                          if (isNum(row.lat) && isNum(row.lng)) {
                            setSelectedCoords([Number(row.lat), Number(row.lng)]);
                          }
                        }}
                      >
                        <td className="p-2">{row.index}</td>
                        <td className="p-2">
                          {dayjs(row.capturedAt).isValid()
                            ? dayjs(row.capturedAt).format("DD/MM/YYYY")
                            : "—"}
                        </td>
                        <td className="p-2">
                          {dayjs(row.capturedAt).isValid()
                            ? dayjs(row.capturedAt).format("HH:mm:ss")
                            : "—"}
                        </td>

                        <td className="p-2">
                          {dayjs(row.uploadedAt).isValid()
                            ? dayjs(row.uploadedAt).format("HH:mm:ss")
                            : "—"}
                        </td>
                        <td className="p-2">{row.eventType}</td>
                        <td className="p-2">{row.transport ?? "—"}</td>
                        <td className="p-2">{row.user ?? "—"}</td>
                        <td className="p-2">{row.lat ?? "—"}</td>
                        <td className="p-2">{row.lng ?? "—"}</td>
                        <td className="p-2">{fmtBattery(row.battery)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="p-3 text-gray-500">
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalTablePages > 1 && (
              <div className="flex justify-end gap-2 mt-2">
                <button
                  disabled={pageTable <= 1}
                  onClick={() => setPageTable((p) => p - 1)}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  disabled={pageTable >= totalTablePages}
                  onClick={() => setPageTable((p) => p + 1)}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
